'use client'

import { useParams, useRouter } from 'next/navigation'
import { useEffect } from 'react'

import { range } from '@/utils/array'
import { FLAG_KEY_DAVI_1503 } from '@/utils/toggle'
import {
  trackClickApplyFilters,
  trackTypeLocalSearch,
  trackViewDataIndukKabKot,
} from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import { DistrictAncestorsResponse } from '@/apis/types'
import {
  useFetchDataIndukFilters,
  useFetchSatpenStats,
  useFetchSatpenStatsByParentWilayah,
} from '@/apis/useDataInduk'

import { Container } from '@/components/Container'
import { DownloadSurveyProvider } from '@/components/DownloadSurvey/DownloadSurveyProvider'
import { EmptyState } from '@/components/EmptyState'
import { HeadingTitleBlock } from '@/components/HeadingTitleBlock/HeadingTitleBlock'
import { DataIndukIcon } from '@/components/Icons/DataIndukIcon'
import { useIsActiveFF } from '@/components/WhitelistFfContext'

import { DATA_INDUK_ROUTE, METATAG } from '@/configs/constants'

import { AreaCard } from './components/AreaCard/AreaCard'
import { AreaCardLoader } from './components/AreaCard/AreaCardLoader'
import { AreaSummaryStats } from './components/AreaSummaryStats/AreaSummaryStats'
import { ButtonDownloadSatpen } from './components/ButtonDownload/ButtonDownloadSatpen'
import { DataIndukSearchBox } from './components/DataIndukSearchBox'
import { ApplyFilterButton } from './components/Filter/ApplyFilterButton'
import { FilterBentuk } from './components/Filter/FilterBentuk'
import { FilterJalur } from './components/Filter/FilterJalur'
import { FilterJenis } from './components/Filter/FilterJenis'
import { FilterSection } from './components/Filter/FilterSection'
import { FilterStatus } from './components/Filter/FilterStatus'
import { LastUpdatedAt } from './components/LastUpdatedAt'
import { LocalSearch } from './components/LocalSearch'
import { NavSection } from './components/NavSection'
import { TitlePage } from './components/TitlePage'
import { DATA_INDUK_TITLE_PREFIX, VARIANT } from './constant'
import {
  getLatestWilayahName,
  getLogoProvinceLink,
  statsToTableArray,
} from './helpers'
import { useFilterState } from './hooks/useFilterState'
import { useKabKotSwicher } from './hooks/useKabKotSwicher'
import { KODE_WILAYAH_LUAR_NEGERI } from './provinces'

export const DataIndukKabKot = ({
  dataBreadcrumb,
}: {
  dataBreadcrumb: DistrictAncestorsResponse
}) => {
  const router = useRouter()
  const params = useParams()
  const provinceId = (params?.provinsi_id as string) || ''
  const kabKotId = (params?.kabkot_id as string) || ''

  const { isActive: isActive_1503 } = useIsActiveFF({
    code: FLAG_KEY_DAVI_1503,
    envVal: process.env.NEXT_PUBLIC_FEATURE_DAVI_1503,
  })

  const {
    queryBentuk,
    queryJalur,
    queryStatus,
    queryJenis,

    selectedJalur,
    selectedBentuk,
    selectedStatus,
    selectedJenis,

    handleChangeJalur,
    handleChangeBentuk,
    handleChangeStatus,
    handleChangeJenis,

    getFilterSearchParams,
    getFilterSearchParamsFromQuery,
  } = useFilterState({
    variant: VARIANT.SATPEN,
    view: 'list',
  })

  const { data: dataFilters, isPending: isPendingFilters } =
    useFetchDataIndukFilters({
      enabled: !!kabKotId,
    })

  const { data: dataStatsByParent, isPending: isPendingStatsByParent } =
    useFetchSatpenStatsByParentWilayah(
      kabKotId,
      {
        bentuk: queryBentuk,
        jalur: queryJalur,
        status: queryStatus,
        jenis: queryJenis,
      },
      {
        enabled: !!kabKotId,
      }
    )

  const { data: dataStats, isPending: isPendingStats } = useFetchSatpenStats(
    kabKotId,
    {
      enabled: !!kabKotId,
    }
  )

  const { filteredData, onSearchByWilayah } = useKabKotSwicher({
    data: dataStatsByParent,
    isPending: isPendingStatsByParent,
  })

  const isLuarNegeri = provinceId === KODE_WILAYAH_LUAR_NEGERI

  const handleFilterSubmission = () => {
    trackClickApplyFilters({
      locationLevel: 'Kabupaten',
      locationId: kabKotId,
      jalur: selectedJalur,
      bentuk: selectedBentuk,
      status: selectedStatus,
      jenis: selectedJenis,
      section: TrackerSection.SATPEN,
      subSection: TrackerSubSection.PAUD_DIKDASMEN,
    })

    const searchParams = getFilterSearchParams({})
    router.replace(searchParams, { scroll: false })
  }

  const handleSubmitLocalSearch = (keywordLocalSearch: string) => {
    if (keywordLocalSearch !== '') {
      trackTypeLocalSearch({
        keyword: keywordLocalSearch,
        locationLevel: 'Kabupaten',
        locationId: kabKotId,
        section: TrackerSection.SATPEN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
    onSearchByWilayah(keywordLocalSearch)
  }

  useEffect(() => {
    if (provinceId && kabKotId) {
      trackViewDataIndukKabKot({ provinceId, kabKotId })
    }
  }, [provinceId, kabKotId])

  return (
    <>
      <HeadingTitleBlock
        withIcon={true}
        withFaqText={true}
        withMapOverlay={true}
        icon={<DataIndukIcon />}
        title={METATAG.DATA_INDUK_SATPEN.title}
        desc={METATAG.DATA_INDUK_SATPEN.description}
        trackerGaEventName="induk"
        trackerPageTitle="Data Induk"
        trackerAdditionalParams={{
          section: TrackerSection.SATPEN,
          sub_section: TrackerSubSection.PAUD_DIKDASMEN,
        }}
      />
      <article>
        <section className="flex flex-col" id="search-section">
          <DataIndukSearchBox
            source={`${DATA_INDUK_ROUTE}/${provinceId}/${kabKotId}`}
            locationId={`${kabKotId}`}
            locationLevel="Kabupaten"
          />
        </section>

        <NavSection
          data={dataBreadcrumb}
          level={2}
          withRoot={true}
          prefixPath={DATA_INDUK_ROUTE}
          className="pt-4"
        />

        <section className="bg-gray-5 py-4" id="table-section">
          <Container>
            {!dataBreadcrumb ? (
              <div className="mb-4 min-h-[400px] w-full rounded-lg bg-white p-6">
                <EmptyState
                  title="Data tidak ditemukan"
                  description="Maaf data tidak dapat ditemukan, silahkan pilih data lain"
                  size="sm"
                />
              </div>
            ) : (
              <>
                <div
                  className="min-h-[500px] w-full rounded-lg bg-white p-6"
                  data-testid="content-wrapper"
                >
                  <AreaSummaryStats
                    title={`${DATA_INDUK_TITLE_PREFIX[VARIANT.SATPEN]} di ${getLatestWilayahName({ data: dataBreadcrumb })}`}
                    withImage={true}
                    level={isLuarNegeri ? 'country' : 'district'}
                    image={`${getLogoProvinceLink({
                      data: dataBreadcrumb,
                      withDefault: true,
                    })}`}
                    stats={
                      !isPendingStats && dataStats
                        ? statsToTableArray(
                            dataStats?.satuanPendidikanStatistics,
                            VARIANT.SATPEN
                          )
                        : []
                    }
                    isPending={isPendingStats}
                  />

                  <div className="flex flex-col items-start justify-between gap-2 pt-6 md:flex-row md:items-center">
                    <TitlePage variant={VARIANT.SATPEN} />
                    {isActive_1503 && (
                      <DownloadSurveyProvider initialSource="DATA_INDUK">
                        <ButtonDownloadSatpen
                          disabled={isPendingStatsByParent}
                          bentuk={queryBentuk}
                          jalur={queryJalur}
                          status={queryStatus}
                          jenis={queryJenis}
                          kodeKabupaten={kabKotId}
                          lastUpdatedAt={
                            dataStatsByParent?.meta.lastUpdatedAt as string
                          }
                          extensions={['xlsx', 'csv']}
                          level="Kabupaten"
                        />
                      </DownloadSurveyProvider>
                    )}
                  </div>

                  {!isPendingFilters && dataFilters && (
                    <div className="mb-4 flex w-full flex-col lg:flex-row lg:items-center lg:justify-between">
                      <FilterSection
                        FilterJalur={
                          <FilterJalur
                            value={selectedJalur}
                            onChange={handleChangeJalur}
                            loadingStatus={'success'}
                            jalur={dataFilters?.filters?.jalurPendidikan}
                          />
                        }
                        FilterBentuk={
                          <FilterBentuk
                            value={selectedBentuk}
                            onChange={handleChangeBentuk}
                            loadingStatus={'success'}
                            bentuk={
                              dataFilters?.filters?.bentukPendidikanSuperGroup
                            }
                          />
                        }
                        FilterStatus={
                          <FilterStatus
                            value={selectedStatus}
                            onChange={handleChangeStatus}
                            loadingStatus={'success'}
                            status={
                              dataFilters?.filters?.statusSatuanPendidikan
                            }
                          />
                        }
                        FilterJenis={
                          <FilterJenis
                            value={selectedJenis}
                            onChange={handleChangeJenis}
                            loadingStatus={'success'}
                            jenis={dataFilters?.filters?.jenisPendidikan}
                          />
                        }
                        ApplyButton={
                          <ApplyFilterButton onClick={handleFilterSubmission} />
                        }
                      />
                      <LocalSearch onSubmit={handleSubmitLocalSearch} />
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                    {isPendingStatsByParent ? (
                      <>
                        {range(1, 5).map((item) => (
                          <AreaCardLoader key={item} />
                        ))}
                      </>
                    ) : (
                      <>
                        {(filteredData || []).map((item) => {
                          return (
                            <AreaCard
                              key={item.district.kodeWilayah}
                              code={item.district.kodeWilayah}
                              title={item.district.namaWilayah}
                              link={`${DATA_INDUK_ROUTE}/${provinceId}/${kabKotId}/${item.district.kodeWilayah}`}
                              additionalParams={getFilterSearchParamsFromQuery()}
                              textLink={`Lihat Detail`}
                              stats={item.satuanPendidikanStatistics}
                            />
                          )
                        })}
                      </>
                    )}
                  </div>
                </div>
                {dataStatsByParent?.meta?.lastUpdatedAt && (
                  <LastUpdatedAt
                    value={dataStatsByParent?.meta.lastUpdatedAt}
                  />
                )}
              </>
            )}
          </Container>
        </section>
      </article>
    </>
  )
}
